/**
 * JustTCG Pricing Utility
 * Fetches per-condition pricing from JustTCG API (TCGPlayer marketplace data).
 * Provides NM/LP/MP/HP/DMG prices with variant awareness (1st Edition, Holo, etc.)
 *
 * API response shape:
 * { data: [{ name, tcgplayerId, variants: [{ condition, printing, price, priceChange7d, priceChange30d, ... }] }] }
 */

const JUSTTCG_API_BASE = 'https://api.justtcg.com/v1';
const JUSTTCG_API_KEY = process.env.REACT_APP_JUSTTCG_API_KEY;

// Map JustTCG condition strings to our short keys
const CONDITION_MAP = {
  'Near Mint': 'nm',
  'Lightly Played': 'lp',
  'Moderately Played': 'mp',
  'Heavily Played': 'hp',
  'Damaged': 'dmg',
};

/**
 * Fetch per-condition pricing from JustTCG for a TCGPlayer product ID.
 * @param {string} tcgplayerId - TCGPlayer product ID (e.g., "84584")
 * @param {string} [variant] - User's selected variant to filter by (e.g., "1st Edition", "Holo")
 * @returns {Object|null} Normalized pricing object or null if unavailable
 */
export async function fetchJustTCGPricing(tcgplayerId, variant) {
  if (!tcgplayerId || !JUSTTCG_API_KEY) return null;

  const perfLabel = `[Perf] fetchJustTCGPricing: ${tcgplayerId}`;
  console.time(perfLabel);

  try {
    const res = await fetch(
      `${JUSTTCG_API_BASE}/cards?game=pokemon&tcgplayerId=${encodeURIComponent(tcgplayerId)}`,
      {
        headers: { 'X-Api-Key': JUSTTCG_API_KEY },
      }
    );

    if (!res.ok) {
      console.error('[JustTCG] API error:', res.status);
      console.timeEnd(perfLabel);
      return null;
    }

    const data = await res.json();
    const cards = data.data || [];

    if (!Array.isArray(cards) || cards.length === 0) {
      console.log('[JustTCG] No results for tcgplayerId:', tcgplayerId);
      console.timeEnd(perfLabel);
      return null;
    }

    // Variants are nested inside cards[0].variants
    const variants = cards[0].variants || [];
    if (variants.length === 0) {
      console.log('[JustTCG] Card found but no variants:', tcgplayerId);
      console.timeEnd(perfLabel);
      return null;
    }

    // Collect all unique printings
    const allVariants = [...new Set(variants.map(v => v.printing).filter(Boolean))];

    // Filter by user's variant if provided
    const variantLower = (variant || '').toLowerCase();
    let matched = variants;
    if (variantLower) {
      const filtered = variants.filter(v => {
        const printing = (v.printing || '').toLowerCase();
        return printing.includes(variantLower) || variantLower.includes(printing);
      });
      if (filtered.length > 0) {
        matched = filtered;
      }
    }

    // If still multiple printings, pick the one with the most condition entries
    const printingGroups = {};
    for (const v of matched) {
      const key = v.printing || 'Unknown';
      if (!printingGroups[key]) printingGroups[key] = [];
      printingGroups[key].push(v);
    }

    const bestPrinting = Object.entries(printingGroups)
      .sort((a, b) => b[1].length - a[1].length)[0];

    const matchedItems = bestPrinting ? bestPrinting[1] : matched;
    const matchedVariant = bestPrinting ? bestPrinting[0] : 'Unknown';

    // Build per-condition map
    const conditions = {};
    for (const v of matchedItems) {
      const condKey = CONDITION_MAP[v.condition];
      if (!condKey) continue;

      conditions[condKey] = {
        price: v.price || null,
        printing: v.printing || null,
        change7d: v.priceChange7d ?? null,
        change30d: v.priceChange30d ?? null,
      };
    }

    if (Object.keys(conditions).length === 0) {
      console.log('[JustTCG] No condition data found for:', tcgplayerId);
      console.timeEnd(perfLabel);
      return null;
    }

    console.log('[JustTCG] Found', Object.keys(conditions).length, 'conditions for variant:', matchedVariant);
    console.timeEnd(perfLabel);

    return {
      conditions,
      variant: matchedVariant,
      allVariants,
      allItems: variants,
      fetchedAt: new Date().toISOString(),
    };
  } catch (err) {
    console.error('[JustTCG] Fetch error:', err.message);
    console.timeEnd(perfLabel);
    return null;
  }
}
